import { Link } from 'react-router-dom';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import GompaMark from './GompaMark';
import Icon from './Icon';
import { api } from '../lib/api';
import { trackEvent } from '../lib/consent';

export default function SiteFooter() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [state, setState] = useState({ status: 'idle', message: '' });

  const subscribe = async (event) => {
    event.preventDefault();
    setState({ status: 'sending', message: '' });
    try {
      const { message } = await api.newsletter(email);
      setState({ status: 'done', message: message || t('footer.subscribed', 'You are on the list. Tashi delek.') });
      trackEvent('newsletter_signup');
      setEmail('');
    } catch (error) {
      setState({ status: 'error', message: error.message });
    }
  };

  return (
    <footer className="site-footer">
      <div className="shell">
        <div className="site-footer__grid">
          <div>
            <Link to="/" className="brand-lockup">
              <GompaMark size={34} />
              <span>
                <span className="brand-lockup__name">GOMPA</span>
                <span className="brand-lockup__strap">{t('footer.brandStrap', 'Monastery & Ritual Services')}</span>
              </span>
              <span className="visually-hidden"> — {t('nav.homeAria', 'home')}</span>
            </Link>
            <p className="site-footer__blurb">
              {t(
                'footer.blurb',
                'Butter lamps, pujas and long-life prayers at Tibetan Buddhist monasteries in India, Nepal and Tibet. Offerings are recited by the resident sangha exactly as you write them.'
              )}
            </p>
          </div>

          <div>
            <h3>{t('footer.offeringsTitle', 'Offerings')}</h3>
            <ul>
              <li><Link to="/explore">{t('footer.exploreMonasteries', 'Explore monasteries')}</Link></li>
              <li><Link to="/offerings">{t('footer.myOfferings', 'My offerings')}</Link></li>
              <li><Link to="/saved">{t('footer.savedMonasteries', 'Saved monasteries')}</Link></li>
              <li><Link to="/about#how">{t('footer.howItWorks', 'How an offering works')}</Link></li>
            </ul>
          </div>

          <div>
            <h3>{t('footer.aboutTitle', 'About')}</h3>
            <ul>
              <li><Link to="/about">{t('footer.whyGompaExists', 'Why GOMPA exists')}</Link></li>
              <li><Link to="/about#faq">{t('footer.questions', 'Questions')}</Link></li>
              <li><Link to="/contact">{t('footer.contact', 'Contact')}</Link></li>
              <li>
                <Link to="/credits">{t('footer.photographyCredits', 'Photography credits')}</Link>
              </li>
            </ul>
          </div>

          <div>
            <h3>{t('footer.stayInTouchTitle', 'Stay in touch')}</h3>
            <form onSubmit={subscribe} className="stack stack--sm">
              <label htmlFor="footer-email" className="visually-hidden">
                {t('footer.emailLabel', 'Email address')}
              </label>
              <input
                id="footer-email"
                type="email"
                required
                className="input"
                placeholder={t('footer.emailPlaceholder', 'you@example.com')}
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                style={{ background: 'rgba(255,255,255,0.08)', borderColor: 'rgba(212,175,55,0.4)', color: '#fff' }}
              />
              <button className="btn btn--gold btn--sm" disabled={state.status === 'sending'}>
                {state.status === 'sending'
                  ? t('footer.sending', 'Sending…')
                  : t('footer.keepMeInformed', 'Keep me informed')}
              </button>
              {state.message && (
                <p style={{ fontSize: 12.5, color: state.status === 'error' ? '#ffb4b4' : 'var(--accent)' }}>
                  {state.message}
                </p>
              )}
            </form>
          </div>
        </div>

        <div className="site-footer__bottom">
          <span className="stack stack--sm">
            <span>{t('footer.copyright', { year: new Date().getFullYear(), defaultValue: `© ${new Date().getFullYear()} GOMPA App LLC. Offerings at Tibetan Buddhist monasteries.` })}</span>
            <span>
              {t(
                'footer.independenceDisclaimer',
                'GOMPA App is a completely independent platform. We are not affiliated with any other monastery portal or international organization sharing a similar descriptive name.'
              )}
            </span>
          </span>
          <span className="row" style={{ gap: 'var(--space-lg)' }}>
            <Link to="/terms">{t('footer.terms', 'Terms of Offering')}</Link>
            <Link to="/privacy">{t('footer.privacy', 'Privacy')}</Link>
            <a href="mailto:gompaapp@gmail.com" className="row" style={{ gap: 6 }}>
              <Icon name="mail" size={14} /> gompaapp@gmail.com
            </a>
          </span>
        </div>
      </div>
    </footer>
  );
}
